import React, { useEffect, useState , useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { getUser, clearAuth } from '../../utils/auth';
import { toast } from 'sonner';
import PatientAIConsent from '../../components/PatientAIConsent';
import MyRequests from './MyRequests';
import {
  Calendar, DollarSign, Clock, TrendingUp, FileText, Download,
  Video, User, Phone, MapPin, Award, Heart, Activity,
  Pill, Bell, AlertCircle, CheckCircle, XCircle, Shield, Upload, X,
  Home, MessageSquare, CreditCard, Settings, LogOut, Menu, ChevronRight,
  Stethoscope, History, HelpCircle, FileCheck, Plane, Building, Hotel,
  Ticket, FolderOpen, StickyNote, Eye, Trash2, Edit, Plus, ExternalLink,
  Car, Utensils, Globe, Navigation, ArrowRight
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import PatientLayout from './PatientLayout';

const iconMap = {
  'Globe': Globe,
  'CheckCircle': CheckCircle,
  'Plane': Plane,
  'Hotel': Hotel,
  'Video': Video,
  'Building': Building,
  'Stethoscope': Stethoscope,
  'Heart': Heart,
  'FileCheck': FileCheck,
  'Calendar': Calendar
};

export default function PatientDashboard() {
  const navigate = useNavigate();
  const [user] = useState(getUser);
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [medicalRecords, setMedicalRecords] = useState([]);
  const [payments, setPayments] = useState([]);
  const [assignedDoctor, setAssignedDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  // Start with sidebar closed on mobile, open on desktop
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);
  const [activeSection, setActiveSection] = useState('dashboard');
  
  // Dialog states
  const [showBookConsultation, setShowBookConsultation] = useState(false);
  const [showMedicationRequest, setShowMedicationRequest] = useState(false);
  const [showUploadRecord, setShowUploadRecord] = useState(false);
  
  // Form states
  const [consultationForm, setConsultationForm] = useState({
    appointmentType: 'consultation',
    preferredDate: '',
    preferredTime: '',
    notes: ''
  });
  const [medicationForm, setMedicationForm] = useState({
    medicationName: '',
    dosage: '',
    reason: ''
  });
  
  // Journey state
  const [journeyTab, setJourneyTab] = useState('overview');
  const [journeyData, setJourneyData] = useState(null);
  const [journeyDocuments, setJourneyDocuments] = useState([]);
  const [journeyNotes, setJourneyNotes] = useState([]);
  const [journeyLoading, setJourneyLoading] = useState(false);
  const [showAddDocument, setShowAddDocument] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState({ title: '', content: '', category: 'General' });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('General');
  const [uploadDocType, setUploadDocType] = useState('other');
  const [uploading, setUploading] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  
  // Video consultation state
  const [activeVideoConsultation, setActiveVideoConsultation] = useState(null);
  const [joiningVideo, setJoiningVideo] = useState(false);
  const [videoRoomUrl, setVideoRoomUrl] = useState(null);
  const [videoToken, setVideoToken] = useState(null);
  
  // Icon mapping for stages loaded from API



  // Handle resize events for responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadDashboardData = useCallback(async () => {
    try {
      const [statsRes, appointmentsRes, recordsRes, paymentsRes, doctorRes] = await Promise.all([
        api.get('/patient/dashboard-stats'),
        api.get('/patient/appointments'),
        api.get('/patient/medical-records'),
        api.get('/patient/payments'),
        api.get('/patient/assigned-doctor')
      ]);

      setStats(statsRes.data.stats);
      const appointmentsData = appointmentsRes.data.appointments;
      setAppointments(appointmentsData);
      setMedicalRecords(recordsRes.data.records);
      setPayments(paymentsRes.data.payments);
      setAssignedDoctor(doctorRes.data.doctor);
      
      // Check for active video consultations
      const videoConsultation = appointmentsData.find(apt => 
        (apt.type === 'video_consultation' || apt.appointment_type === 'video_consultation') && 
        apt.status === 'scheduled'
      );
      
      if (videoConsultation) {
        setActiveVideoConsultation(videoConsultation);
      }
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Join video consultation
  const handleJoinVideoCall = async (appointmentId) => {
    if (!appointmentId) {
      toast.error('Appointment not found');
      return;
    }
    
    setJoiningVideo(true);
    try {
      // First, check if there's an active video session for this appointment
      const sessionCheck = await api.get(`/video/active-session/${appointmentId}`).catch(() => null);
      
      if (!sessionCheck || !sessionCheck.data?.room_name) {
        toast.error('Doctor has not started the consultation yet. Please wait for the doctor to start the call.');
        setJoiningVideo(false);
        return;
      }
      
      const roomName = sessionCheck.data.room_name;
      
      // Join the room
      const response = await api.post('/video/join-room', { room_name: roomName });
      
      if (response.data.success) {
        setVideoRoomUrl(response.data.room_url);
        setVideoToken(response.data.token);
        
        // Redirect to video call page or open in new tab
        const videoUrl = `${response.data.room_url}?t=${response.data.token}`;
        window.open(videoUrl, '_blank');
        
        toast.success('Joining video consultation...');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to join video call';
      toast.error(errorMsg);
    } finally {
      setJoiningVideo(false);
    }
  };

  const loadJourneyData = useCallback(async () => {
    setJourneyLoading(true);
    try {
      const [journeyRes, docsRes, notesRes] = await Promise.all([
        api.get('/patient/journey'),
        api.get('/patient/journey/documents'),
        api.get('/patient/journey/notes')
      ]);

      // Map icon strings to actual components
      const journeyWithIcons = journeyRes.data.journey;
      if (journeyWithIcons?.stages) {
        journeyWithIcons.stages = journeyWithIcons.stages.map(stage => ({
          ...stage,
          icon: iconMap[stage.icon] || Calendar
        }));
      }

      setJourneyData(journeyWithIcons);
      setJourneyDocuments(docsRes.data.documents || []);
      setJourneyNotes(notesRes.data.notes || []);
    } catch (error) {
      // Set default empty structure on error
      setJourneyData({
        visa: { status: 'not_started', type: null, number: null, issueDate: null, expiryDate: null, country: null },
        travel: { outbound: null, return: null },
        hotel: null,
        hospital: null,
        stages: [
          { id: 1, name: 'Visa Application', status: 'pending', date: null, icon: Globe },
          { id: 2, name: 'Visa Approved', status: 'pending', date: null, icon: CheckCircle },
          { id: 3, name: 'Flight Booked', status: 'pending', date: null, icon: Plane },
          { id: 4, name: 'Hotel Booked', status: 'pending', date: null, icon: Hotel },
          { id: 5, name: 'Pre-Travel Consultation', status: 'pending', date: null, icon: Video },
          { id: 6, name: 'Departure', status: 'pending', date: null, icon: Plane },
          { id: 7, name: 'Hospital Admission', status: 'pending', date: null, icon: Building },
          { id: 8, name: 'Treatment/Surgery', status: 'pending', date: null, icon: Stethoscope },
          { id: 9, name: 'Recovery', status: 'pending', date: null, icon: Heart },
          { id: 10, name: 'Discharge', status: 'pending', date: null, icon: FileCheck },
          { id: 11, name: 'Return Flight', status: 'pending', date: null, icon: Plane },
          { id: 12, name: 'Follow-up Care', status: 'pending', date: null, icon: Calendar }
        ]
      });
    } finally {
      setJourneyLoading(false);
    }
  }, []);

  // Load journey data when journey section is active
  useEffect(() => {
    if (activeSection === 'journey' && !journeyData) {
      loadJourneyData();
    }
  }, [activeSection, journeyData, loadJourneyData]);


  // Journey document upload handler
  const handleJourneyDocumentUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('category', uploadCategory);
      formData.append('doc_type', uploadDocType);
      
      const response = await api.post('/patient/journey/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Document uploaded successfully!');
      setShowAddDocument(false);
      setUploadFile(null);
      setUploadCategory('General');
      setUploadDocType('other');
      
      // Refresh documents list
      const docsRes = await api.get('/patient/journey/documents');
      setJourneyDocuments(docsRes.data.documents || []);
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  // Journey document delete handler
  const handleDeleteJourneyDocument = async (docId) => {
    try {
      await api.delete(`/patient/journey/documents/${docId}`);
      toast.success('Document deleted');
      setJourneyDocuments(prev => prev.filter(doc => (doc.doc_id || doc.id) !== docId));
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  // Journey document download handler
  const handleDownloadJourneyDocument = async (docId, fileName) => {
    try {
      const response = await api.get(`/patient/journey/documents/${docId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Download started');
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  // Journey note create handler
  const handleCreateJourneyNote = async (e) => {
    e.preventDefault();
    if (!newNote.title || !newNote.content) {
      toast.error('Please fill in title and content');
      return;
    }
    
    setSavingNote(true);
    try {
      await api.post('/patient/journey/notes', newNote);
      toast.success('Note created successfully!');
      setShowAddNote(false);
      setNewNote({ title: '', content: '', category: 'General' });
      
      // Refresh notes list
      const notesRes = await api.get('/patient/journey/notes');
      setJourneyNotes(notesRes.data.notes || []);
    } catch (error) {
      toast.error('Failed to create note');
    } finally {
      setSavingNote(false);
    }
  };

  // Journey note delete handler
  const handleDeleteJourneyNote = async (noteId) => {
    try {
      await api.delete(`/patient/journey/notes/${noteId}`);
      toast.success('Note deleted');
      setJourneyNotes(prev => prev.filter(note => (note.note_id || note.id) !== noteId));
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const handleBookConsultation = async (e) => {
    e.preventDefault();
    try {
      await api.post('/patient/appointments/book', consultationForm);
      toast.success('Consultation booked successfully!');
      setShowBookConsultation(false);
      setConsultationForm({ appointmentType: 'consultation', preferredDate: '', preferredTime: '', notes: '' });
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to book consultation');
    }
  };

  const handleMedicationRequest = async (e) => {
    e.preventDefault();
    try {
      await api.post('/patient/medication-request', medicationForm);
      toast.success('Medication request submitted successfully!');
      setShowMedicationRequest(false);
      setMedicationForm({ medicationName: '', dosage: '', reason: '' });
    } catch (error) {
      toast.error('Failed to submit medication request');
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', uploadFile);
    
    try {
      await api.post('/patient/medical-records/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Medical record uploaded successfully!');
      setShowUploadRecord(false);
      setUploadFile(null);
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to upload file');
    }
  };

  const handleDownloadRecord = async (recordId, fileName) => {
    try {
      const response = await api.get(`/patient/medical-records/${recordId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Download started');
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const getAppointmentTypeColor = (type) => {
    const colors = {
      'consultation': 'bg-blue-100 text-blue-700',
      'pre-op': 'bg-purple-100 text-purple-700',
      'surgery': 'bg-red-100 text-red-700',
      'post-op': 'bg-green-100 text-green-700',
      'follow-up': 'bg-orange-100 text-orange-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <PatientLayout activeSection={activeSection} setActiveSection={setActiveSection}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-slate-500 dark:text-slate-400">Loading dashboard...</p>
          </div>
        </div>
      </PatientLayout>
    );
  }

  // Render dashboard content based on active section
  const renderDashboardContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return renderDashboardOverview();
      case 'journey':
        return renderJourneySection();
      case 'requests':
        return <MyRequests />;
      case 'appointments':
        return renderAppointmentsSection();
      case 'treatments':
        return renderTreatmentsSection();
      case 'records':
        return renderRecordsSection();
      case 'messages':
        return renderMessagesSection();
      case 'payments':
        return renderPaymentsSection();
      case 'history':
        return renderHistorySection();
      case 'settings':
        return renderSettingsSection();
      case 'ai-privacy':
        return <PatientAIConsent />;
      default:
        return renderDashboardOverview();
    }
  };

  // Dashboard Overview - Mobile First Design
  const renderDashboardOverview = () => (
    <div className="space-y-6" data-testid="patient-dashboard">
      {/* Welcome Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 dark:from-emerald-600 dark:to-teal-600 p-6 text-white">
        <div className="relative z-10">
          <h1 className="font-heading text-2xl md:text-3xl font-bold">
            Welcome back, {user?.full_name?.split(' ')[0] || 'Patient'}!
          </h1>
          <p className="mt-2 text-emerald-100 text-sm md:text-base">
            Track your treatment journey, manage appointments, and stay connected with your care team
          </p>
        </div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-32 translate-x-32" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24" />
      </div>

      {/* Next Appointment Card */}
      {appointments.length > 0 && (
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="p-4 md:p-6 bg-sky-50 dark:bg-sky-900/20 border-b border-sky-100 dark:border-sky-800">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-sky-100 dark:bg-sky-900/50 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-sky-600 dark:text-sky-400" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-slate-900 dark:text-white">Your Next Appointment</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    with {appointments[0].doctor_name || 'Doctor'}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                className="hidden sm:flex"
                onClick={() => setActiveSection('appointments')}
              >
                View Details
              </Button>
            </div>
            <div className="mt-4 flex flex-wrap gap-4 text-sm">
              <span className="text-slate-600 dark:text-slate-400">
                {new Date(appointments[0].date || appointments[0].appointment_date).toLocaleDateString()}
              </span>
              {appointments[0].time && (
                <span className="text-slate-600 dark:text-slate-400">{appointments[0].time}</span>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <button
          onClick={() => setShowBookConsultation(true)}
          className="flex flex-col items-center justify-center p-4 md:p-6 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-emerald-300 dark:hover:border-emerald-700 hover:shadow-lg hover:shadow-emerald-500/10 transition-all group"
          data-testid="quick-action-book"
        >
          <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
            <Calendar className="w-6 h-6 text-emerald-600 dark:text-emerald-400" strokeWidth={1.5} />
          </div>
          <span className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-300">Book Consultation</span>
        </button>
        
        <button
          onClick={() => setShowMedicationRequest(true)}
          className="flex flex-col items-center justify-center p-4 md:p-6 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-purple-300 dark:hover:border-purple-700 hover:shadow-lg hover:shadow-purple-500/10 transition-all group"
          data-testid="quick-action-medication"
        >
          <div className="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
            <Pill className="w-6 h-6 text-purple-600 dark:text-purple-400" strokeWidth={1.5} />
          </div>
          <span className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-300">Request Medication</span>
        </button>
        
        <button
          onClick={() => setActiveSection('journey')}
          className="flex flex-col items-center justify-center p-4 md:p-6 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-sky-300 dark:hover:border-sky-700 hover:shadow-lg hover:shadow-sky-500/10 transition-all group"
          data-testid="quick-action-journey"
        >
          <div className="w-12 h-12 rounded-xl bg-sky-50 dark:bg-sky-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
            <Navigation className="w-6 h-6 text-sky-600 dark:text-sky-400" strokeWidth={1.5} />
          </div>
          <span className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-300">Schedule Follow-up</span>
        </button>
        
        <button
          onClick={() => setShowUploadRecord(true)}
          className="flex flex-col items-center justify-center p-4 md:p-6 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-lg hover:shadow-amber-500/10 transition-all group"
          data-testid="quick-action-upload"
        >
          <div className="w-12 h-12 rounded-xl bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
            <Upload className="w-6 h-6 text-amber-600 dark:text-amber-400" strokeWidth={1.5} />
          </div>
          <span className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-300">Upload Reports</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-sky-50 dark:bg-sky-900/30 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-sky-600 dark:text-sky-400" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats?.upcoming_appointments || 1}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Upcoming</p>
            </div>
          </div>
        </Card>
        
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-emerald-600 dark:text-emerald-400" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">${stats?.total_paid || '5,000'}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Total Paid</p>
            </div>
          </div>
        </Card>
        
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center">
              <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">N/A</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Days Until Surgery</p>
            </div>
          </div>
        </Card>
        
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-amber-600 dark:text-amber-400" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats?.follow_up_score || 85}%</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Follow-up Score</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Active Video Consultation */}
      {activeVideoConsultation && (
        <Card className="bg-gradient-to-r from-sky-500 to-sky-600 dark:from-sky-600 dark:to-sky-700 border-0 text-white">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                  <Video className="w-7 h-7" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-lg">Video Consultation Available</h3>
                  <p className="text-sky-100 text-sm">Your doctor is ready to see you</p>
                </div>
              </div>
              <Button 
                onClick={() => handleJoinVideoCall(activeVideoConsultation._id || activeVideoConsultation.id)}
                disabled={joiningVideo}
                className="bg-white text-sky-600 hover:bg-sky-50"
              >
                {joiningVideo ? 'Joining...' : 'Join Call Now'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Appointment Timeline */}
        <Card className="lg:col-span-2 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <div className="p-4 md:p-6 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">Appointment Timeline</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Your upcoming and recent appointments</p>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setActiveSection('appointments')}
                className="text-emerald-600 dark:text-emerald-400"
              >
                View All
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
          <div className="p-4 md:p-6">
            {appointments.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600" strokeWidth={1} />
                <p className="mt-4 text-slate-500 dark:text-slate-400">No appointments scheduled</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setShowBookConsultation(true)}
                >
                  Book Your First Appointment
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {appointments.slice(0, 4).map((apt, idx) => (
                  <div 
                    key={apt._id || apt.id || idx}
                    className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 flex items-center justify-center shrink-0">
                      <Calendar className="w-5 h-5 text-slate-600 dark:text-slate-400" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="font-medium text-slate-900 dark:text-white truncate">
                          {apt.type || apt.appointment_type || 'Consultation'}
                        </h4>
                        <Badge className={`shrink-0 ${
                          apt.status === 'scheduled' ? 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400' :
                          apt.status === 'completed' ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' :
                          'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                        }`}>
                          {apt.status || 'Scheduled'}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">
                        with {apt.doctor_name || 'Doctor'}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {new Date(apt.date || apt.appointment_date).toLocaleDateString()}
                        {apt.time && ` at ${apt.time}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Your Doctor */}
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <div className="p-4 md:p-6 border-b border-slate-200 dark:border-slate-800">
            <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">Your Doctor</h2>
          </div>
          <div className="p-4 md:p-6">
            {assignedDoctor ? (
              <div className="text-center">
                <Avatar className="w-20 h-20 mx-auto ring-4 ring-emerald-100 dark:ring-emerald-900/50">
                  <AvatarImage src={assignedDoctor.avatar} />
                  <AvatarFallback className="bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 text-2xl">
                    {(assignedDoctor.full_name || 'Dr').split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <h3 className="mt-4 font-heading font-semibold text-slate-900 dark:text-white">
                  Dr. {assignedDoctor.full_name}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {assignedDoctor.specialization || 'Specialist'}
                </p>
                <div className="mt-4 flex items-center justify-center gap-2">
                  <Badge className="bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                    {assignedDoctor.rating || 4.8} Rating
                  </Badge>
                </div>
                <Button className="w-full mt-6" variant="outline">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Message Doctor
                </Button>
              </div>
            ) : (
              <div className="text-center py-8">
                <User className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600" strokeWidth={1} />
                <p className="mt-4 text-slate-500 dark:text-slate-400">No doctor assigned yet</p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );

  // Simplified section renderers that display existing content sections
  const renderJourneySection = () => (
    <div className="space-y-6">
      <div className="text-center py-8">
        <Navigation className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" strokeWidth={1} />
        <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-white">Journey Section</h2>
        <p className="text-slate-500 dark:text-slate-400 mt-2">Your treatment journey details</p>
      </div>
    </div>
  );

  const renderAppointmentsSection = () => (
    <div className="space-y-6">
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <div className="p-6">
          <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-white mb-4">All Appointments</h2>
          {appointments.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600" strokeWidth={1} />
              <p className="mt-4 text-slate-500 dark:text-slate-400">No appointments scheduled</p>
              <Button variant="outline" className="mt-4" onClick={() => setShowBookConsultation(true)}>
                Book Consultation
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {appointments.map((apt, idx) => (
                <div key={apt._id || apt.id || idx} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-slate-900 dark:text-white">{apt.type || 'Consultation'}</h4>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{apt.doctor_name || 'Doctor'}</p>
                    </div>
                    <Badge>{apt.status || 'Scheduled'}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );

  const renderTreatmentsSection = () => (
    <div className="space-y-6">
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <div className="p-6 text-center">
          <Stethoscope className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600" strokeWidth={1} />
          <h2 className="mt-4 font-heading text-xl font-semibold text-slate-900 dark:text-white">My Treatments</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-2">Your treatment plans will appear here</p>
        </div>
      </Card>
    </div>
  );

  const renderRecordsSection = () => (
    <div className="space-y-6">
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-white">Medical Records</h2>
            <Button variant="outline" size="sm" onClick={() => setShowUploadRecord(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Upload
            </Button>
          </div>
          {medicalRecords.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600" strokeWidth={1} />
              <p className="mt-4 text-slate-500 dark:text-slate-400">No medical records uploaded</p>
            </div>
          ) : (
            <div className="space-y-3">
              {medicalRecords.map((record, idx) => (
                <div key={record._id || idx} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-slate-500" />
                    <span className="text-slate-700 dark:text-slate-300">{record.filename || record.name}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDownloadRecord(record._id || record.id, record.filename)}>
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );

  const renderMessagesSection = () => (
    <div className="space-y-6">
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <div className="p-6 text-center">
          <MessageSquare className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600" strokeWidth={1} />
          <h2 className="mt-4 font-heading text-xl font-semibold text-slate-900 dark:text-white">Messages</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-2">Your conversations with your care team</p>
        </div>
      </Card>
    </div>
  );

  const renderPaymentsSection = () => (
    <div className="space-y-6">
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <div className="p-6">
          <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-white mb-4">Payment History</h2>
          {payments.length === 0 ? (
            <div className="text-center py-8">
              <CreditCard className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600" strokeWidth={1} />
              <p className="mt-4 text-slate-500 dark:text-slate-400">No payment history</p>
            </div>
          ) : (
            <div className="space-y-3">
              {payments.map((payment, idx) => (
                <div key={payment._id || idx} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">${payment.amount}</p>
                    <p className="text-sm text-slate-500">{payment.description || 'Payment'}</p>
                  </div>
                  <Badge className={payment.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                    {payment.status || 'Pending'}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );

  const renderHistorySection = () => (
    <div className="space-y-6">
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <div className="p-6 text-center">
          <History className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600" strokeWidth={1} />
          <h2 className="mt-4 font-heading text-xl font-semibold text-slate-900 dark:text-white">History</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-2">Your past treatments and visits</p>
        </div>
      </Card>
    </div>
  );

  const renderSettingsSection = () => (
    <div className="space-y-6">
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <div className="p-6">
          <h2 className="font-heading text-xl font-semibold text-slate-900 dark:text-white mb-4">Account Settings</h2>
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
              <p className="text-sm text-slate-500 dark:text-slate-400">Email</p>
              <p className="font-medium text-slate-900 dark:text-white">{user?.email}</p>
            </div>
            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
              <p className="text-sm text-slate-500 dark:text-slate-400">Full Name</p>
              <p className="font-medium text-slate-900 dark:text-white">{user?.full_name}</p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );

  return (
    <PatientLayout activeSection={activeSection} setActiveSection={setActiveSection}>
      {renderDashboardContent()}
      
      {/* Dialogs */}
      {/* Book Consultation Dialog */}
      <Dialog open={showBookConsultation} onOpenChange={setShowBookConsultation}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Book Consultation</DialogTitle>
            <DialogDescription>Schedule an appointment with your doctor</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleBookConsultation}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Appointment Type</Label>
                <Select value={consultationForm.appointmentType} onValueChange={(v) => setConsultationForm({...consultationForm, appointmentType: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="consultation">Consultation</SelectItem>
                    <SelectItem value="follow-up">Follow-up</SelectItem>
                    <SelectItem value="video">Video Call</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Preferred Date</Label>
                <Input type="date" value={consultationForm.preferredDate} onChange={(e) => setConsultationForm({...consultationForm, preferredDate: e.target.value})} />
              </div>
              <div>
                <Label>Notes (Optional)</Label>
                <Textarea value={consultationForm.notes} onChange={(e) => setConsultationForm({...consultationForm, notes: e.target.value})} placeholder="Any specific concerns..." />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowBookConsultation(false)}>Cancel</Button>
              <Button type="submit">Book Appointment</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Medication Request Dialog */}
      <Dialog open={showMedicationRequest} onOpenChange={setShowMedicationRequest}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request Medication</DialogTitle>
            <DialogDescription>Request a prescription refill or new medication</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleMedicationRequest}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Medication Name</Label>
                <Input value={medicationForm.medicationName} onChange={(e) => setMedicationForm({...medicationForm, medicationName: e.target.value})} placeholder="Enter medication name" />
              </div>
              <div>
                <Label>Dosage</Label>
                <Input value={medicationForm.dosage} onChange={(e) => setMedicationForm({...medicationForm, dosage: e.target.value})} placeholder="e.g., 500mg" />
              </div>
              <div>
                <Label>Reason</Label>
                <Textarea value={medicationForm.reason} onChange={(e) => setMedicationForm({...medicationForm, reason: e.target.value})} placeholder="Why do you need this medication?" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowMedicationRequest(false)}>Cancel</Button>
              <Button type="submit">Submit Request</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Record Dialog */}
      <Dialog open={showUploadRecord} onOpenChange={setShowUploadRecord}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Medical Record</DialogTitle>
            <DialogDescription>Upload your medical documents</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleFileUpload}>
            <div className="py-4">
              <div className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl p-8 text-center">
                <Upload className="w-10 h-10 mx-auto text-slate-400" />
                <p className="mt-2 text-sm text-slate-500">Drag & drop or click to upload</p>
                <Input 
                  type="file" 
                  className="mt-4" 
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowUploadRecord(false)}>Cancel</Button>
              <Button type="submit" disabled={!uploadFile}>Upload</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </PatientLayout>
  );
}
